import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ImageOff } from "lucide-react";

interface VenuePhotoCarouselProps {
  readonly photos: readonly string[];
  readonly venueName: string;
}

export function VenuePhotoCarousel({ photos, venueName }: VenuePhotoCarouselProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [brokenIndexes, setBrokenIndexes] = useState<ReadonlySet<number>>(new Set());

  const handleImageError = useCallback((index: number) => {
    setBrokenIndexes((prev) => new Set([...prev, index]));
  }, []);

  if (photos.length === 0) {
    return (
      <div
        data-testid="venue-photos-empty"
        className="flex h-48 items-center justify-center rounded-lg bg-muted"
      >
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <ImageOff className="h-8 w-8" />
          <span className="font-sans text-sm font-normal">{t("venues.detail.noPhotos")}</span>
        </div>
      </div>
    );
  }

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(index, photos.length - 1));
    setCurrent(clamped);
  };

  const isBroken = brokenIndexes.has(current);

  return (
    <div data-testid="venue-photo-carousel" className="relative overflow-hidden rounded-lg">
      {isBroken ? (
        <div className="flex h-48 w-full items-center justify-center bg-muted">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <ImageOff className="h-6 w-6" />
            <span className="font-sans text-xs font-normal">{t("venues.detail.photoError")}</span>
          </div>
        </div>
      ) : (
        <img
          src={photos[current]}
          alt={`${venueName} — ${current + 1}/${photos.length}`}
          className="h-48 w-full object-cover"
          onError={() => handleImageError(current)}
        />
      )}

      {photos.length > 1 && (
        <>
          <button
            type="button"
            data-testid="venue-photo-prev"
            onClick={(e) => {
              e.stopPropagation();
              goTo(current - 1);
            }}
            disabled={current === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
            aria-label={t("venues.detail.previousPhoto")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            data-testid="venue-photo-next"
            onClick={(e) => {
              e.stopPropagation();
              goTo(current + 1);
            }}
            disabled={current === photos.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white transition-opacity hover:bg-black/70 disabled:opacity-30"
            aria-label={t("venues.detail.nextPhoto")}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-2 py-0.5 font-sans text-xs font-normal text-white">
            {current + 1} / {photos.length}
          </div>
        </>
      )}
    </div>
  );
}
